import { createAsyncThunk } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'

import { api } from 'src/Services/api'
import { APP } from 'src/Configs/App'
import {
  SpotifyCampaign,
  CampaignStatusEnum,
  CampaignTypeEnum,
  TiktokCampaign,
  TiktokCampaignStatus,
} from 'src/Types/campaigns'

import { ErrorCode, NameSpace } from '../types'
import { MetaCampaign } from '../ad-campaignApi'

import {
  mapMetaCampaigns,
  mapSpotifyCampaign,
  mapTiktokCampaign,
} from './helpers'

export type SpotifyCampaignsListResponse = Array<{
  id: number
  type: CampaignTypeEnum
  startDate: string
  endDate: string
  status: CampaignStatusEnum
  relaunched: boolean
  reviewedReviews: number
  trackName: string
  artistName: string
  matchingType?: string
  trackImage: string
  createdAt?: string
  isPreRelease: boolean
  releaseDate: string
  placementsCount: number
  trackSpotifyId: string | null
}>

export interface TikTokCampaignsListResponse {
  campaigns: Array<{
    createdAt: string
    endDate: string
    genres: Array<{ id: number; genreName: string }>
    id: number
    influencersAcceptedAmount: number
    languages: Array<{ id: number; languageName: string }>
    startDate: string
    status: TiktokCampaignStatus
    submittedVideosAmount: number
    trackAuthorName: string
    trackCoverUrl: string
    trackPlayUrl: string
    trackTitle: string
    type: CampaignTypeEnum
    updatedAt: string
  }>
}

export enum MetaStrategyType {
  PLAYLIST = 'playlist',
  TRACK = 'track',
}

export enum MetaCampaignStatus {
  APPROVED = 'approved',
  ENDED = 'ended',
  RUNNING = 'running',
  SCHEDULED = 'scheduled',
}

export type MetaCampaignsListResponse = Array<{
  id: number
  ownerName: string
  ownerSpotifyUrl: string
  coverUrl: string
  resourceName: string
  resourceSpotifyId: string
  resourceSpotifyUrl: string
  strategy: MetaStrategyType
  startDate: string
  endDate: string
  status: MetaCampaignStatus
  createdAt: string
  relaunched?: never
  reviewedReviews?: never
  allReview?: never
  submittedVideosAmount: number
}>

export const getCampaignsList = createAsyncThunk<{
  campaigns: Array<TiktokCampaign | SpotifyCampaign | MetaCampaign>
  spotifyCampaigns: Array<SpotifyCampaign>
  tiktokCampaigns: Array<TiktokCampaign>
}>(
  `${NameSpace.CampaignList}/getCampaignsList`,
  async (_, { rejectWithValue }) => {
    try {
      const { data: spotifyData } = await api.get<SpotifyCampaignsListResponse>(
        `${APP.TIKTOK_SERVER}/artist/spotify-campaigns`,
      )
      const { data: tiktokData } = await api.get<TikTokCampaignsListResponse>(
        `${APP.TIKTOK_SERVER}/artist/tiktok/campaigns`,
      )

      const { data: metaData } = await api.get<MetaCampaignsListResponse>(
        `${APP.TIKTOK_SERVER}/artist/ad-campaigns`,
      )

      const spotifyCampaigns = mapSpotifyCampaign(spotifyData)
      const tiktokCampaigns = mapTiktokCampaign(tiktokData)
      const metaCampaigns = mapMetaCampaigns(metaData)

      const campaigns = [
        ...spotifyCampaigns,
        ...tiktokCampaigns,
        ...metaCampaigns,
      ].sort((a, b) => {
        const dateA = new Date(String(a.createdAt))
        const dateB = new Date(String(b.createdAt))
        return dateB.getTime() - dateA.getTime()
      })

      return {
        campaigns,
        spotifyCampaigns,
        tiktokCampaigns,
      }
    } catch (error) {
      const { message, response } = error as AxiosError
      return rejectWithValue({
        message,
        code: response?.status ?? ErrorCode.InternalServerError,
      })
    }
  },
)
